import type { MenteeProfile, MentorProfile, UserProfile } from '#shared/types/User'

/**
 * Section-based mentoring profile completion.
 * Mirrors the same sections shown in ProfileWizard for mentor/mentee types:
 * intent, basic, additional, experience, education, languages, achievements, hobbies, training
 *
 * Each section has equal weight → completedSections / totalSections * 100
 */

interface SectionCheck {
    id: string
    label: string
    completed: boolean
}

function hasContent(html?: string): boolean {
    if (!html) return false
    const text = html.replace(/<[^>]*>/g, '').trim()
    return text.length > 0
}

export function getMentorSections(
    user: UserProfile,
    mentorProfile?: MentorProfile
): SectionCheck[] {
    return [
        { id: 'intent', label: 'Témy môjho mentorovania', completed: !!mentorProfile?.interests?.length },
        { id: 'basic', label: 'Meno, mesto, foto', completed: !!(user.firstName && user.lastName && user.geoCity) },
        { id: 'additional', label: 'Viac o mne', completed: !!(hasContent(user.hobby) && user.interests?.length) },
        { id: 'experience', label: 'Profesionálne skúsenosti', completed: !!user.jobs?.length },
        { id: 'education', label: 'Vzdelanie', completed: !!(user.highSchools?.length || user.universities?.length) },
        { id: 'languages', label: 'Jazykové znalosti', completed: !!user.languages?.length },
        { id: 'achievements', label: 'Doplnkové aktivity a úspechy', completed: !!user.achievements?.length },
        { id: 'hobbies', label: 'Hobby a záujmy', completed: hasContent(user.hobbiesAndInterests) },
        { id: 'training', label: 'Kvíz o mentoringu', completed: !!mentorProfile?.trainingCompletedAt }
    ]
}

export function getMenteeSections(
    user: UserProfile,
    menteeProfile?: MenteeProfile
): SectionCheck[] {
    return [
        { id: 'intent', label: 'Témy môjho rozvoja', completed: !!menteeProfile?.goals?.trim() },
        { id: 'basic', label: 'Meno, mesto, foto', completed: !!(user.firstName && user.lastName && user.geoCity) },
        { id: 'additional', label: 'Viac o mne', completed: !!(hasContent(user.hobby) && user.interests?.length) },
        { id: 'experience', label: 'Profesionálne skúsenosti', completed: !!user.jobs?.length },
        { id: 'education', label: 'Vzdelanie', completed: !!(user.highSchools?.length || user.universities?.length) },
        { id: 'languages', label: 'Jazykové znalosti', completed: !!user.languages?.length },
        { id: 'achievements', label: 'Doplnkové aktivity a úspechy', completed: !!user.achievements?.length },
        { id: 'hobbies', label: 'Hobby a záujmy', completed: hasContent(user.hobbiesAndInterests) },
        { id: 'training', label: 'Kvíz o mentoringu', completed: !!menteeProfile?.trainingCompletedAt }
    ]
}

export function calculateSectionCompletion(sections: SectionCheck[]): number {
    const completed = sections.filter(s => s.completed).length
    return Math.round((completed / sections.length) * 100)
}
